<template>
    <div class="score-page">
        <!-- ---------- 头部 ---------- -->
        <header class="sc-hero">
            <div class="hero-top">
                <span class="hero-back" @click="goBack">‹</span>
                <span class="hero-title">成绩曲线</span>
                <span class="hero-back ghost"></span>
            </div>

            <div class="hero-card" v-if="latest">
                <div class="hc-head">
                    <span class="hc-name">{{ latest.name }}</span>
                    <span class="hc-date">{{ latest.date }}</span>
                </div>
                <div class="hc-score">
                    <em>{{ latest.score }}</em>
                    <span class="hc-full">/ {{ latest.full }}</span>
                    <span class="hc-delta" :class="latest.delta >= 0 ? 'up' : 'down'">
                        {{ latest.delta >= 0 ? '↑' : '↓' }}{{ Math.abs(latest.delta) }} 分
                    </span>
                </div>
                <div class="hc-foot">
                    <span>班级排名 {{ latest.rank }} / {{ latest.classSize }}</span>
                    <span>
                        名次
                        <b :class="latest.rankDelta >= 0 ? 'up' : 'down'">
                            {{ latest.rankDelta >= 0 ? '↑' : '↓' }}{{ Math.abs(latest.rankDelta) }}
                        </b>
                    </span>
                </div>
            </div>
        </header>

        <main class="sc-body">
            <!-- ---------- 科目选择 ---------- -->
            <div class="chips">
                <span
                    class="chip"
                    v-for="s in series"
                    :key="s.key"
                    :class="{ on: s.key === currentKey }"
                    @click="currentKey = s.key"
                >
                    {{ s.name }}
                </span>
            </div>

            <!-- ---------- 曲线 ---------- -->
            <section class="chart-card" v-if="chart">
                <div class="chart-head">
                    <span class="chart-title">{{ current.name }} 走势</span>
                    <span class="chart-sub">{{ exams.length }} 次考试 · 满分 {{ current.full }}</span>
                </div>

                <div class="chart-wrap">
                    <svg class="chart" :viewBox="'0 0 ' + chart.W + ' ' + chart.H" preserveAspectRatio="xMidYMid meet">
                        <defs>
                            <linearGradient id="scoreArea" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stop-color="#378add" stop-opacity="0.26"></stop>
                                <stop offset="100%" stop-color="#378add" stop-opacity="0"></stop>
                            </linearGradient>
                        </defs>

                        <!-- 网格与刻度 -->
                        <g v-for="(t, i) in chart.ticks" :key="'t' + i">
                            <line
                                :x1="chart.padL"
                                :x2="chart.W - chart.padR"
                                :y1="t.y"
                                :y2="t.y"
                                stroke="#eef1f6"
                                stroke-width="1"
                            ></line>
                            <text
                                :x="chart.padL - 12"
                                :y="t.y + 7"
                                text-anchor="end"
                                font-size="20"
                                fill="#a3abba"
                            >
                                {{ t.v }}
                            </text>
                        </g>

                        <!-- 面积 + 折线 -->
                        <path :d="chart.area" fill="url(#scoreArea)"></path>
                        <polyline
                            :points="chart.line"
                            fill="none"
                            stroke="#378add"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        ></polyline>

                        <!-- 数据点 + 分值 -->
                        <g v-for="(p, i) in chart.pts" :key="'p' + i">
                            <circle
                                :cx="p.x"
                                :cy="p.y"
                                :r="i === chart.pts.length - 1 ? 9 : 6"
                                :fill="i === chart.pts.length - 1 ? '#378add' : '#fff'"
                                stroke="#378add"
                                stroke-width="3"
                            ></circle>
                            <text
                                :x="p.x"
                                :y="p.y - 22"
                                text-anchor="middle"
                                font-size="24"
                                font-weight="600"
                                :fill="i === chart.pts.length - 1 ? '#185fa5' : '#5f6b7d'"
                            >
                                {{ p.v }}
                            </text>
                        </g>

                        <!-- 横轴：考试名称 + 日期 -->
                        <g v-for="(p, i) in chart.pts" :key="'x' + i">
                            <text
                                :x="p.x"
                                :y="chart.baseY + 36"
                                text-anchor="middle"
                                font-size="22"
                                fill="#8a94a6"
                            >
                                {{ p.name }}
                            </text>
                            <text
                                :x="p.x"
                                :y="chart.baseY + 62"
                                text-anchor="middle"
                                font-size="20"
                                fill="#c2c9d6"
                            >
                                {{ p.date }}
                            </text>
                        </g>
                    </svg>
                </div>

                <div class="chart-foot">
                    <div class="cf-item">
                        <p class="cf-label">最高</p>
                        <p class="cf-num">{{ chart.max }}</p>
                    </div>
                    <div class="cf-item">
                        <p class="cf-label">最低</p>
                        <p class="cf-num">{{ chart.min }}</p>
                    </div>
                    <div class="cf-item">
                        <p class="cf-label">平均</p>
                        <p class="cf-num">{{ currentAvg }}</p>
                    </div>
                    <div class="cf-item">
                        <p class="cf-label">最近排名</p>
                        <p class="cf-num">{{ current.rank }}/{{ current.classSize }}</p>
                    </div>
                </div>
            </section>

            <!-- ---------- 明细 ---------- -->
            <section class="block">
                <div class="block-head">
                    <span class="block-title">{{ current.name }} · 历次明细</span>
                </div>

                <div class="rows">
                    <div class="row" v-for="r in rows" :key="r.key">
                        <div class="row-main">
                            <p class="row-name">{{ r.name }}</p>
                            <p class="row-date">{{ r.date }}</p>
                        </div>
                        <div class="row-score">
                            <em>{{ r.score }}</em>
                            <span class="row-full">/ {{ current.full }}</span>
                        </div>
                        <div class="row-rank">
                            <p class="rr-num">第 {{ r.rank }} 名</p>
                            <p class="rr-delta" :class="r.rankDelta > 0 ? 'up' : r.rankDelta < 0 ? 'down' : ''">
                                {{
                                    r.rankDelta > 0
                                        ? '↑' + r.rankDelta + ' 名'
                                        : r.rankDelta < 0
                                        ? '↓' + Math.abs(r.rankDelta) + ' 名'
                                        : '持平'
                                }}
                            </p>
                        </div>
                    </div>
                </div>

                <p class="tip">演示数据 · 接入成绩表后按同样的结构返回即可</p>
            </section>
        </main>
    </div>
</template>

<script>
import { fetchScoreTrend } from '../../utils/studyData';

/** 曲线图坐标系（单位为 750 稿像素，viewBox 内等比缩放） */
const CHART = { W: 690, H: 330, padL: 60, padR: 34, padT: 40, padB: 76 };

export default {
    name: 'AppScore',
    data() {
        return {
            exams: [],
            series: [],
            latest: null,
            currentKey: 'total'
        };
    },
    computed: {
        current() {
            const hit = this.series.filter(s => s.key === this.currentKey)[0];
            return hit || this.series[0] || null;
        },
        currentAvg() {
            if (!this.current) {
                return '—';
            }
            const list = this.current.scores;
            const sum = list.reduce((a, b) => a + b, 0);
            return Math.round((sum / list.length) * 10) / 10;
        },
        /** 折线图几何：点位、折线、面积、网格刻度全部算好给模板用 */
        chart() {
            const s = this.current;
            if (!s || !s.scores.length) {
                return null;
            }
            const W = CHART.W;
            const H = CHART.H;
            const innerW = W - CHART.padL - CHART.padR;
            const innerH = H - CHART.padT - CHART.padB;
            const scores = s.scores;
            const max = Math.max.apply(null, scores);
            const min = Math.min.apply(null, scores);
            // 上下各留一点余量，成绩全相同时也不至于贴边
            const pad = Math.max((max - min) * 0.35, 3);
            const top = max + pad;
            const bottom = min - pad;
            const baseY = CHART.padT + innerH;

            const xAt = i => (scores.length === 1 ? CHART.padL + innerW / 2 : CHART.padL + (innerW * i) / (scores.length - 1));
            const yAt = v => CHART.padT + innerH - ((v - bottom) / (top - bottom)) * innerH;

            const pts = scores.map((v, i) => ({
                x: xAt(i),
                y: yAt(v),
                v: v,
                name: (this.exams[i] && this.exams[i].name) || '',
                date: (this.exams[i] && this.exams[i].date) || ''
            }));

            const mid = Math.round(((max + min) / 2) * 10) / 10;

            return {
                W: W,
                H: H,
                padL: CHART.padL,
                padR: CHART.padR,
                baseY: baseY,
                pts: pts,
                line: pts.map(p => p.x + ',' + p.y).join(' '),
                area:
                    'M' +
                    pts[0].x +
                    ',' +
                    baseY +
                    ' L' +
                    pts.map(p => p.x + ',' + p.y).join(' L') +
                    ' L' +
                    pts[pts.length - 1].x +
                    ',' +
                    baseY +
                    ' Z',
                ticks: [max, mid, min].map(v => ({ v: v, y: yAt(v) })),
                max: max,
                min: min
            };
        },
        /** 明细：最新的排最前 */
        rows() {
            const s = this.current;
            if (!s) {
                return [];
            }
            return this.exams
                .map((e, i) => ({
                    key: e.key,
                    name: e.name,
                    date: e.date,
                    score: s.scores[i],
                    rank: s.ranks[i],
                    // 名次涨跌：数字变小才是进步，所以用「上一次名次 - 这一次名次」
                    rankDelta: i > 0 ? s.ranks[i - 1] - s.ranks[i] : 0
                }))
                .reverse();
        }
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            fetchScoreTrend()
                .then(data => {
                    if (!data) {
                        return;
                    }
                    this.exams = data.exams || [];
                    this.series = data.series || [];
                    this.latest = data.latest || null;
                    if (!this.series.filter(s => s.key === this.currentKey).length && this.series.length) {
                        this.currentKey = this.series[0].key;
                    }
                })
                .catch(() => {
                    this.exams = [];
                    this.series = [];
                    this.latest = null;
                });
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }
            this.$router.replace({ path: '/home' });
        }
    }
};
</script>

<style lang="less" scoped>
.score-page {
    min-height: 100vh;
    box-sizing: border-box;
    padding-bottom: calc(170px + constant(safe-area-inset-bottom));
    padding-bottom: calc(170px + env(safe-area-inset-bottom));
    background: #f4f6fa;
}

/* ---------------- 头部 ---------------- */
.sc-hero {
    position: relative;
    padding: 40px 40px 66px;
    color: #fff;
    background: var(--blueTheme, #0066b3);
    border-radius: 0 0 44px 44px;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: -160px;
        right: -110px;
        width: 400px;
        height: 400px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.13);
    }
}

.hero-top {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.hero-back {
    width: 60px;
    font-size: 46px;
    line-height: 1;
    color: #fff;

    &.ghost {
        visibility: hidden;
    }
    &:active {
        opacity: 0.7;
    }
}

.hero-title {
    font-size: 34px;
    font-weight: 600;
    letter-spacing: 1px;
}

.hero-card {
    position: relative;
    z-index: 1;
    margin-top: 30px;
    padding: 28px 30px 24px;
    border-radius: 26px;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.22);
}

.hc-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 24px;
    opacity: 0.88;
}

.hc-score {
    display: flex;
    align-items: baseline;
    margin-top: 14px;

    em {
        font-style: normal;
        font-size: 62px;
        font-weight: 700;
        line-height: 1;
    }
}

.hc-full {
    margin-left: 10px;
    font-size: 24px;
    opacity: 0.82;
}

.hc-delta {
    margin-left: auto;
    padding: 6px 18px;
    border-radius: 999px;
    font-size: 22px;
    background: rgba(255, 255, 255, 0.2);

    &.down {
        color: #ffe0d6;
    }
}

.hc-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    font-size: 22px;
    opacity: 0.9;

    b {
        font-weight: 600;
    }
}

/* ---------------- 科目 chips ---------------- */
.sc-body {
    padding: 0 30px;
}

.chips {
    display: flex;
    margin-top: 28px;
    overflow-x: auto;

    &::-webkit-scrollbar {
        display: none;
    }
}

.chip {
    flex: none;
    margin-right: 16px;
    padding: 12px 30px;
    border-radius: 999px;
    font-size: 24px;
    color: #5f6b7d;
    background: #fff;
    border: 1px solid #e8ecf2;

    &.on {
        color: #fff;
        background: var(--blueTheme, #0066b3);
        border-color: var(--blueTheme, #0066b3);
    }
}

/* ---------------- 曲线卡 ---------------- */
.chart-card {
    margin-top: 26px;
    padding: 30px 24px 24px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.06);
}

.chart-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 0 10px;
}

.chart-title {
    font-size: 30px;
    font-weight: 600;
    color: #22283a;
}

.chart-sub {
    font-size: 20px;
    color: #a3abba;
}

.chart-wrap {
    margin-top: 10px;
}

.chart {
    display: block;
    width: 100%;
    /* 与 viewBox 690 × 330 等比 */
    height: 300px;
}

.chart-foot {
    display: flex;
    margin-top: 10px;
    padding-top: 22px;
    border-top: 1px solid #f2f4f8;
}

.cf-item {
    position: relative;
    flex: 1;
    min-width: 0;
    text-align: center;

    & + .cf-item::before {
        content: '';
        position: absolute;
        left: 0;
        top: 4px;
        bottom: 4px;
        width: 1px;
        background: #eef1f6;
    }
}

.cf-label {
    margin: 0;
    font-size: 20px;
    color: #8a94a6;
}

.cf-num {
    margin: 8px 0 0;
    font-size: 28px;
    font-weight: 600;
    color: #22283a;
}

/* ---------------- 明细 ---------------- */
.block {
    margin-top: 34px;
}

.block-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 56px;
    padding: 0 8px;
}

.block-title {
    font-size: 30px;
    font-weight: 600;
    color: #22283a;
}

.rows {
    padding: 0 30px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.06);
}

.row {
    display: flex;
    align-items: center;
    padding: 26px 0;

    & + .row {
        border-top: 1px solid #f2f4f8;
    }
}

.row-main {
    flex: none;
    width: 180px;
}

.row-name {
    margin: 0;
    font-size: 26px;
    color: #2b3445;
}

.row-date {
    margin: 8px 0 0;
    font-size: 20px;
    color: #a3abba;
}

.row-score {
    flex: 1;
    min-width: 0;
    text-align: right;

    em {
        font-style: normal;
        font-size: 34px;
        font-weight: 600;
        color: #22283a;
    }
}

.row-full {
    margin-left: 6px;
    font-size: 20px;
    color: #a3abba;
}

.row-rank {
    flex: none;
    width: 150px;
    margin-left: 24px;
    text-align: right;
}

.rr-num {
    margin: 0;
    font-size: 22px;
    color: #5f6b7d;
}

.rr-delta {
    margin: 8px 0 0;
    font-size: 20px;
    color: #a3abba;

    &.up {
        color: #085041;
    }
    &.down {
        color: #d85a30;
    }
}

.up {
    color: #085041;
}

.down {
    color: #d85a30;
}

.tip {
    margin: 30px 0 0;
    text-align: center;
    font-size: 20px;
    color: #c2c9d6;
}
</style>
